import classNames from 'classnames';
import React, { useContext, useRef, useState } from 'react';
import { Popover } from '@meitu/saas-ui';
import {
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type IFontTextGradient,
    type ITextLayerProps,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useEditor } from '@/hooks/use-editor';
import { useLocaleStore } from '@/i18n';
import { TextEffectDisplay } from '../../components';
import { TextPanelContext } from '../../context';
import { useLayerProps } from '../../hooks';
import TextEffectModal from '../text-effect-modal';
import './style.less';

type Props = React.ComponentProps<'div'> & {
    onClick?: () => void;
    free?: boolean;
    hidePresets?: boolean;
    /**
     * 展示进入详情按钮
     */
    showDetailBtn?: boolean;
    /**
     * 展示花字 tab
     */
    showArtText?: boolean;
};

type ListenProps = {
    color: string;
    textStroke: IFontStroke[];
    textShadow: IFontShadow[];
    textGlow: IFontGlow[];
    textGradient?: IFontTextGradient;
    textBackground?: IFontBackground[];
};

const DEFAULT_PROPS: ListenProps = {
    color: '#000000ff',
    textStroke: [],
    textShadow: [],
    textGlow: [],
    textGradient: { colors: [], angle: 0 },
    textBackground: [],
};

export const TextEffectEntry: React.FC<Props> = ({
    style,
    free,
    hidePresets,
    showDetailBtn = true,
    showArtText = true,
    onClick,
}) => {
    const { layerId, recordId, isIntl, theme, artTexts } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();
    const editor = useEditor();

    const [open, setOpen] = useState<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const popoverRef = useRef<any>(null);

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return DEFAULT_PROPS;

        const layerData = layer.data as ITextLayerProps;
        const { color, textStroke, textShadow, textGlow, textGradient, textBackground } = layerData;

        return {
            color,
            textStroke,
            textShadow,
            textGlow,
            textGradient,
            textBackground,
        };
    }) as ListenProps;

    const { color, textStroke, textShadow, textGlow, textGradient, textBackground } = props;

    return (
        <div className="sub-panel-entry" style={style}>
            <Popover
                trigger="click"
                ref={popoverRef}
                content={
                    <TextEffectModal
                        layerId={layerId}
                        isIntl={isIntl}
                        recordId={recordId}
                        free={free}
                        hidePresets={hidePresets}
                        theme={theme}
                        artTexts={showArtText ? artTexts : undefined}
                        onClose={() => {
                            // 模拟弹窗外点击关闭弹窗
                            popoverRef.current?.onClick?.();
                        }}
                    />
                }
                placement="left"
                overlayClassName="text-layer-panel-popover"
                destroyTooltipOnHide
                onOpenChange={(val) => setOpen(val)}
            >
                <div className={classNames('sub-panel-entry__content', { 'sub-panel-entry__content--open': open })}>
                    <div className="sub-panel-entry__thumbnail">
                        <TextEffectDisplay
                            usage="entry"
                            color={color}
                            textStroke={textStroke}
                            textShadow={textShadow}
                            textGlow={textGlow}
                            textGradient={textGradient}
                            textBackground={textBackground}
                        />
                    </div>
                    <div className="sub-panel-entry__title">{$t((v) => v.textStyleSelector.specialEffect)}</div>
                    <IconFont name={open ? 'ChevronUpBlack' : 'ChevronDownBlack'} fontSize={12} />
                </div>
            </Popover>

            {showDetailBtn ? (
                <div className="sub-panel-entry__btn" onClick={() => onClick?.()}>
                    <IconFont name="DoubleAdjustBold" fontSize={16} />
                </div>
            ) : null}
        </div>
    );
};
